<template>
  <v-sheet style="width: 100%; height=100%">
    <v-row justify="center" align="center" class="ma-0 py-2 px-6">
      <h2>Task progress</h2>
      <v-spacer></v-spacer>
      <h2 class="mx-2">{{ progress }}% complete</h2>
      <v-btn
        :outlined="!isMobile"
        color="primary"
        tile
        @click="updatePopupVisible = true"
        :class="{ 'update-progress-mobile': isMobile }"
      >
        Update progress
      </v-btn>
    </v-row>
    <v-row class="ma-0 px-2">
      <common-data-table
        :headers="headers"
        collectionName="progressUpdates"
        :dataBasePath="parentPath"
        extraSortBy="date"
        extraSortByDirection="desc"
      >
        <template v-slot:item="{ item, index }">
          <tr :key="item.id">
            <template v-if="isMobile">
              <td class="pt-4">
                <v-col cols="12" class="pa-0 ma-0">
                  <v-row
                    class="ma-0 pa-0"
                    justify="space-between"
                    align="center"
                  >
                    <v-row class="ma-0 pa-0" align="center">
                      <user-avatar :userInfo="item.reportedBy"></user-avatar>
                      <span class="ml-2 text-section-title">{{
                        formattedDate(item.date.toDate())
                      }}</span>
                    </v-row>
                    <p :class="{ lastProgress: index === 0 }">
                      {{ item.progress }}%
                    </p>
                  </v-row>
                  <v-row class="ma-0 pl-12">
                    <p>{{ item.notes }}</p>
                  </v-row>
                </v-col>
              </td>
            </template>
            <template v-else>
              <td>
                <v-row class="ma-0 pa-0" align="center">
                  <user-avatar :userInfo="item.reportedBy"></user-avatar>
                </v-row>
              </td>
              <td>{{ formattedDate(item.date.toDate()) }}</td>
              <td>{{ item.notes }}</td>
              <td>
                <span :class="{ lastProgress: index === 0 }"
                  >{{ item.progress }}%</span
                >
              </td>
            </template>
          </tr>
        </template>
      </common-data-table>
    </v-row>

    <v-dialog v-model="updatePopupVisible" max-width="600px" persistent>
      <common-editing-popup
        title="Update progress"
        :canSubmit="!postingUpdate"
        @submit="submitProgress"
        @close="updatePopupVisible = false"
        saveBtnTitle="Update"
        :originalItem="{ progress, notes: '' }"
        :loading="postingUpdate"
      >
        <template v-slot:default="{ editedItem }">
          <job-progress-update
            :progress.sync="editedItem.progress"
            :notes.sync="editedItem.notes"
            :disabled="postingUpdate"
          ></job-progress-update>
        </template>
      </common-editing-popup>
    </v-dialog>
  </v-sheet>
</template>

<script>
import CommonEditingPopup from '@/components/CommonEditingPopup.vue'
import CommonDataTable from '@/components/CommonDataTable.vue'
import UserAvatar from '@/components/UserAvatar.vue'

import JobProgressUpdate from './JobProgressUpdate.vue'
import { mapActions, mapGetters } from 'vuex'
import { functions } from '@/firebaseConfig'
import { formattedDate } from '@/common'

export default {
  props: {
    jobId: {
      type: String,
      required: true,
    },
    progress: {
      type: Number,
      default: 0,
    },
  },
  components: {
    CommonEditingPopup,
    JobProgressUpdate,
    CommonDataTable,
    UserAvatar,
  },
  data: () => ({
    updatePopupVisible: false,
    postingUpdate: false,
    headers: [
      {
        text: 'Assignee',
        value: 'reportedBy.firstName',
        sortable: false,
      },
      {
        text: 'Date',
        value: 'date',
        sortable: false,
      },
      {
        text: 'Notes',
        value: 'notes',
        sortable: false,
      },
      {
        text: 'Completed',
        value: 'progress',
        sortable: false,
      },
    ],
    formattedDate,
  }),
  computed: {
    ...mapGetters(['companyId', 'isMobile']),
    parentPath() {
      return `companies/${this.companyId}/jobs/${this.jobId}`
    },
  },
  methods: {
    ...mapActions(['showError', 'showSuccess']),
    async submitProgress(data) {
      if (data.progress === this.progress && data.notes.trim().length === 0) {
        this.updatePopupVisible = false
        return
      }

      // Submit Job update to server
      try {
        this.postingUpdate = true
        const postUpdate = functions.httpsCallable('job-addProgress')
        const postData = {
          companyId: this.companyId,
          jobId: this.$route.params.id,
          ...data,
        }

        await postUpdate(postData)
        this.updatePopupVisible = false
        this.showSuccess('Task progress updated successfully')
      } catch (error) {
        console.error(error)
        this.showError(
          error.message ??
            'Failed to post update, please check your data and try again later'
        )
      } finally {
        this.postingUpdate = false
      }
    },
  },
}
</script>

<style lang="sass" scoped>
.lastProgress
  @include button-3

.update-progress-mobile
  position: fixed
  bottom: 0
  left: 0
  width: 100%
  z-index: 9999
  min-height: 57px
</style>
